import { CommunityXpActionKey } from './community-gamification.policy';
import { utcDateKey as formatUtcDateKey } from '../analytics/circle-revenue-analytics';

export const CommunityBadgeAwardType = {
  ACTION_COUNT: 'action_count',
  LEVEL_REACHED: 'level_reached',
  STREAK_DAYS: 'streak_days',
  MANUAL: 'manual',
} as const;

export type CommunityBadgeAwardTypeValue =
  (typeof CommunityBadgeAwardType)[keyof typeof CommunityBadgeAwardType];

export const COMMUNITY_BADGE_NAME_MAX_CHARS = 80;
export const COMMUNITY_BADGE_DESCRIPTION_MAX_CHARS = 240;
export const COMMUNITY_BADGE_SLUG_MAX_CHARS = 64;
export const COMMUNITY_BADGE_ICON_MAX_CHARS = 8;
export const COMMUNITY_BADGE_MAX_PER_COMMUNITY = 30;
export const COMMUNITY_CHALLENGE_TITLE_MAX_CHARS = 120;
export const COMMUNITY_CHALLENGE_DESCRIPTION_MAX_CHARS = 500;
export const COMMUNITY_CHALLENGE_GOAL_TARGET_MAX = 1000;
export const COMMUNITY_CHALLENGE_MAX_PER_COMMUNITY = 20;
export const COMMUNITY_DAILY_STREAK_TYPE = 'daily_activity';
export const COMMUNITY_STREAK_GRACE_DAYS_MAX = 7;
export const COMMUNITY_STREAK_FREEZE_DAYS = 1;
export const COMMUNITY_BADGE_IMAGE_URL_MAX_CHARS = 2048;
export const COMMUNITY_CONCURRENT_CHALLENGES_MAX = 10;

const VALID_ACTION_KEYS = new Set<string>(Object.values(CommunityXpActionKey));

export type CommunityBadgeAwardConfig = {
  actionKey?: string;
  count?: number;
  level?: number;
  days?: number;
};

export type CommunityStreakState = {
  currentDays: number;
  longestDays: number;
  lastActivityDate: string | null;
  streakFreezeUntil?: string | null;
};

export type CommunityStreakAdvanceResult = {
  state: CommunityStreakState;
  broke: boolean;
  previousDays: number;
};

export function daysBetweenUtcDateKeys(from: string, to: string): number {
  const fromDate = new Date(`${from}T00:00:00.000Z`);
  const toDate = new Date(`${to}T00:00:00.000Z`);
  return Math.round((toDate.getTime() - fromDate.getTime()) / (24 * 60 * 60 * 1000));
}

export function normalizeCommunityStreakGraceDays(value: number | null | undefined): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(Math.max(Math.trunc(parsed), 0), COMMUNITY_STREAK_GRACE_DAYS_MAX);
}

export function normalizeCommunityBadgeImageUrl(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  if (!/^https?:\/\//i.test(trimmed)) return null;
  return trimmed.slice(0, COMMUNITY_BADGE_IMAGE_URL_MAX_CHARS);
}

export function previousUtcDateKey(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return formatUtcDateKey(date);
}

export function normalizeCommunityBadgeSlug(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim().toLowerCase();
  if (!trimmed) return null;
  const slug = trimmed
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, COMMUNITY_BADGE_SLUG_MAX_CHARS);
  return slug || null;
}

export function normalizeCommunityBadgeIcon(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '🏅';
  return trimmed.slice(0, COMMUNITY_BADGE_ICON_MAX_CHARS);
}

function trimText(value: string | null | undefined, max: number): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

export function normalizeCommunityBadgeAwardConfig(
  awardType: CommunityBadgeAwardTypeValue,
  config: CommunityBadgeAwardConfig | null | undefined,
): { ok: true; config: CommunityBadgeAwardConfig } | { ok: false; message: string } {
  const input = config ?? {};
  if (awardType === CommunityBadgeAwardType.MANUAL) {
    return { ok: true, config: {} };
  }
  if (awardType === CommunityBadgeAwardType.ACTION_COUNT) {
    const actionKey = (input.actionKey ?? '').trim();
    const count = Number(input.count);
    if (!actionKey || !VALID_ACTION_KEYS.has(actionKey)) {
      return { ok: false, message: 'Badge action_count requires a valid actionKey' };
    }
    if (!Number.isFinite(count) || count < 1 || count > COMMUNITY_CHALLENGE_GOAL_TARGET_MAX) {
      return { ok: false, message: 'Badge action_count requires count between 1 and 1000' };
    }
    return { ok: true, config: { actionKey, count: Math.trunc(count) } };
  }
  if (awardType === CommunityBadgeAwardType.LEVEL_REACHED) {
    const level = Number(input.level);
    if (!Number.isFinite(level) || level < 1 || level > 50) {
      return { ok: false, message: 'Badge level_reached requires level between 1 and 50' };
    }
    return { ok: true, config: { level: Math.trunc(level) } };
  }
  if (awardType === CommunityBadgeAwardType.STREAK_DAYS) {
    const days = Number(input.days);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      return { ok: false, message: 'Badge streak_days requires days between 1 and 365' };
    }
    return { ok: true, config: { days: Math.trunc(days) } };
  }
  return { ok: false, message: 'Unsupported badge award type' };
}

export function validateCommunityBadgeDefinition(input: {
  name?: string | null;
  slug?: string | null;
  awardType: CommunityBadgeAwardTypeValue;
  awardConfig?: CommunityBadgeAwardConfig | null;
}): { ok: true; name: string; slug: string; awardConfig: CommunityBadgeAwardConfig } | { ok: false; message: string } {
  const name = trimText(input.name, COMMUNITY_BADGE_NAME_MAX_CHARS);
  const slug = normalizeCommunityBadgeSlug(input.slug ?? input.name);
  if (!name) return { ok: false, message: 'Badge name is required' };
  if (!slug) return { ok: false, message: 'Badge slug is required' };
  const configResult = normalizeCommunityBadgeAwardConfig(input.awardType, input.awardConfig);
  if (!configResult.ok) return configResult;
  return { ok: true, name, slug, awardConfig: configResult.config };
}

export function validateCommunityChallengeDefinition(input: {
  title?: string | null;
  goalActionKey?: string | null;
  goalTarget?: number | null;
  startsAt?: Date | null;
  endsAt?: Date | null;
}): { ok: true; title: string; goalActionKey: string; goalTarget: number; startsAt: Date; endsAt: Date } | { ok: false; message: string } {
  const title = trimText(input.title, COMMUNITY_CHALLENGE_TITLE_MAX_CHARS);
  const goalActionKey = (input.goalActionKey ?? '').trim();
  const goalTarget = Number(input.goalTarget);
  const startsAt = input.startsAt ?? null;
  const endsAt = input.endsAt ?? null;
  if (!title) return { ok: false, message: 'Challenge title is required' };
  if (!goalActionKey || !VALID_ACTION_KEYS.has(goalActionKey)) {
    return { ok: false, message: 'Challenge requires a valid goal action' };
  }
  if (!Number.isFinite(goalTarget) || goalTarget < 1 || goalTarget > COMMUNITY_CHALLENGE_GOAL_TARGET_MAX) {
    return { ok: false, message: 'Challenge goal target must be between 1 and 1000' };
  }
  if (!startsAt || !endsAt || Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return { ok: false, message: 'Challenge start and end dates are required' };
  }
  if (endsAt <= startsAt) {
    return { ok: false, message: 'Challenge end must be after start' };
  }
  return {
    ok: true,
    title,
    goalActionKey,
    goalTarget: Math.trunc(goalTarget),
    startsAt,
    endsAt,
  };
}

export function advanceDailyActivityStreak(
  state: CommunityStreakState,
  activityDate: string,
  options?: { graceDays?: number; streakFreezeUntil?: string | null },
): CommunityStreakAdvanceResult {
  if (state.lastActivityDate === activityDate) {
    return { state, broke: false, previousDays: state.currentDays };
  }

  const graceDays = normalizeCommunityStreakGraceDays(options?.graceDays);
  const freezeActive =
    Boolean(options?.streakFreezeUntil) && (options?.streakFreezeUntil as string) >= activityDate;

  if (!state.lastActivityDate) {
    return {
      state: {
        currentDays: 1,
        longestDays: Math.max(state.longestDays, 1),
        lastActivityDate: activityDate,
        streakFreezeUntil: state.streakFreezeUntil ?? null,
      },
      broke: false,
      previousDays: 0,
    };
  }

  const gap = daysBetweenUtcDateKeys(state.lastActivityDate, activityDate);
  if (gap === 1) {
    const currentDays = state.currentDays + 1;
    return {
      state: {
        currentDays,
        longestDays: Math.max(state.longestDays, currentDays),
        lastActivityDate: activityDate,
        streakFreezeUntil: state.streakFreezeUntil ?? null,
      },
      broke: false,
      previousDays: state.currentDays,
    };
  }

  if (gap <= graceDays + 1 || freezeActive) {
    return {
      state: {
        ...state,
        lastActivityDate: activityDate,
      },
      broke: false,
      previousDays: state.currentDays,
    };
  }

  const previousDays = state.currentDays;
  return {
    state: {
      currentDays: 1,
      longestDays: Math.max(state.longestDays, 1),
      lastActivityDate: activityDate,
      streakFreezeUntil: null,
    },
    broke: previousDays >= 2,
    previousDays,
  };
}

/** @deprecated Use advanceDailyActivityStreak return value `.state` in new code. */
export function advanceDailyActivityStreakLegacy(
  state: CommunityStreakState,
  activityDate: string,
): CommunityStreakState {
  return advanceDailyActivityStreak(state, activityDate).state;
}

export function isChallengeActive(startsAt: Date, endsAt: Date, now = new Date()): boolean {
  return now >= startsAt && now <= endsAt;
}

export function evaluateBadgeEligibility(params: {
  awardType: CommunityBadgeAwardTypeValue;
  awardConfig: CommunityBadgeAwardConfig;
  actionCounts: Record<string, number>;
  memberLevel: number;
  streakDays: number;
}): boolean {
  if (params.awardType === CommunityBadgeAwardType.MANUAL) return false;
  if (params.awardType === CommunityBadgeAwardType.ACTION_COUNT) {
    const actionKey = params.awardConfig.actionKey ?? '';
    const count = params.awardConfig.count ?? 0;
    return (params.actionCounts[actionKey] ?? 0) >= count;
  }
  if (params.awardType === CommunityBadgeAwardType.LEVEL_REACHED) {
    return params.memberLevel >= (params.awardConfig.level ?? 0);
  }
  if (params.awardType === CommunityBadgeAwardType.STREAK_DAYS) {
    return params.streakDays >= (params.awardConfig.days ?? 0);
  }
  return false;
}

export function buildCommunityMemberBadgeIdempotencyKey(
  communityId: string,
  userId: string,
  badgeId: string,
): string {
  return `badge:${communityId}:${userId}:${badgeId}`;
}
